'use client';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
import { $getRoot, TextNode } from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import ExampleTheme from "../rich-text/themes";
import "../rich-text/styles.css";
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { ImageNode } from '../rich-text/nodes/image-node';
import { InlineImageNode } from '../rich-text/nodes/inline-image-node';
import { YouTubeNode } from '../rich-text/nodes/you-tube-node';
import { FigmaNode } from '../rich-text/nodes/figma-node';
import { PageBreakNode } from '../rich-text/nodes/page-break-node';
import { CustomHeadingNode, CustomTextNode } from '../rich-text';
import { LayoutItemNode } from '../rich-text/nodes/layout-item-node';
import { LayoutContainerNode } from '../rich-text/nodes/layout-container-node';
import { CollapsibleContainerNode } from '../rich-text/nodes/collapsible-container-node';
import { CollapsibleTitleNode } from '../rich-text/nodes/collapsible-title-node';
import { CollapsibleContentNode } from '../rich-text/nodes/collapsible-content-node';
import { StickyNode } from '../rich-text/nodes/sticky-node';

function preprocessHtml(html: string): Document {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('img').forEach((img) => {
    if (img.getAttribute('width') === 'inherit') img.removeAttribute('width');
    if (img.getAttribute('height') === 'inherit') img.removeAttribute('height');
  });

  return doc;
}

function ContentImporterPlugin({ content }: { content: string }) {
  const [editor] = useLexicalComposerContext();
  const lastRef = useRef('');

  useEffect(() => {
    const incoming = (content ?? '').trim();
    if (!incoming) return;
    if (incoming === lastRef.current) return;
    lastRef.current = incoming;

    editor.update(() => {
      try {
        if (incoming.startsWith('{"root"')) {
          const editorState = editor.parseEditorState(incoming);
          editor.setEditorState(editorState);
        } else {
          const dom = preprocessHtml(incoming);
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();
          root.append(...nodes);
        }
      } catch (e) {
        console.error('Failed to import content:', e);
      }
    });
  }, [editor, content]);

  return null;
}

export function ReadOnlyContentDisplay({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  const initialConfig = {
    namespace: 'ReadOnlyContentDisplay',
    editable: false,
    theme: ExampleTheme,
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CustomTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => {
          const customNode = new CustomTextNode(node.getTextContent());
          customNode.setFormat(node.getFormat());
          customNode.setStyle(node.getStyle());
          return customNode;
        },
      },
      TableNode,
      TableCellNode,
      TableRowNode,
      CodeNode,
      CodeHighlightNode,
      HorizontalRuleNode,
      ImageNode,
      InlineImageNode,
      YouTubeNode,
      FigmaNode,
      PageBreakNode,
      CustomHeadingNode,
      LayoutItemNode,
      LayoutContainerNode,
      CollapsibleContainerNode,
      CollapsibleTitleNode,
      CollapsibleContentNode,
      StickyNode,
    ],
    onError(error: Error) {
      console.error(error);
    },
  };

  return (
    <div className={`${className || ''} editor-shell read-only-editor-shell`.trim()}>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={<ContentEditable className="ContentEditable__root editor-input" />}
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <TablePlugin hasCellMerge={true} hasCellBackgroundColor={true} />
        <ListPlugin />
        <LinkPlugin />
        <ContentImporterPlugin content={html} />
      </LexicalComposer>
    </div>
  );
}
